import { useCallback, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { useVoiceChatStore } from "@/features/voice-chat";
import { useCurrentChatId } from "./use-current-chat-id";
import { Message } from "@/types";
import { useToast } from "@/features/toast";
import { apiClient } from "@/api/config/api-client";
import { API_ENDPOINTS } from "@/api/config/api-endpoints";
import { QueryKeys } from "@/api/config/query-keys";
import { useMessageFeedback } from "@/api/hooks";
import { useLLMSelection } from "@/features/llm-selection";
import { modelsPaths } from "@/modules";
import {
  useStreamingChat,
  useStreamingStatus,
} from "@/features/chat/hooks/use-streaming-chat";
import {
  getChatKey,
  useStreamingStore,
} from "@/features/chat/store/streaming-store";

interface MessageInputResponse {
  statusCode: number;
  success: boolean;
  message: string;
  data: {
    model: {
      _id: string;
      [key: string]: any;
    };
    input: {
      _id: string;
      createdAt: string;
      updatedAt: string;
      messageId: string;
      inputData: Record<string, any>;
    };
    message: {
      _id: string;
      createdAt: string;
      updatedAt: string;
      userId: string;
      task: string;
      modelInfo: {
        modelName: string;
      };
      price: number;
      chatId: string;
      prompt: Array<{
        _id: string;
        type: string;
        content: string | null;
        library: any;
      }>;
      response: Array<{
        _id: string;
        type: string;
        content: string | null;
        library: {
          _id: string;
          title: string;
          type: string;
          file: {
            type: string;
            size: number;
            folder: string;
            fileUrl: string;
          };
        } | null;
        isCard: boolean;
        cardContent: any[];
      }>;
      [key: string]: any;
    };
  };
}

type MessageAction =
  | "like"
  | "dislike"
  | "speak"
  | "stop"
  | "simplify"
  | "edit";

export const useMessageHandlers = (messages: Message[] = []) => {
  // ✅ Voice chat artık store-based
  const { speak, stopSpeaking, isVoiceChatActive, isSpeaking } =
    useVoiceChatStore();
  const currentChatId = useCurrentChatId();
  const navigate = useNavigate();
  const { error: errorToast } = useToast();
  const queryClient = useQueryClient();
  const { mutateAsync: updateMessageFeedback } = useMessageFeedback();
  const { t } = useTranslation();
  const { selectedModel, selectedModelName } = useLLMSelection();
  const { sendStreamingMessage } = useStreamingChat();
  const { isStreaming } = useStreamingStatus(currentChatId);
  const setPendingUserMessage = useStreamingStore(
    (state) => state.setPendingUserMessage
  );

  // handleMessageAction'ın referansını stabil tutmak için mesajları ref'te tut.
  // (messages her streaming chunk'ında yeni array olur; doğrudan dependency
  // yapılırsa handler her token'da yeniden yaratılır ve memoize edilmiş
  // mesaj satırlarının tamamı yeniden render edilir.)
  const messagesRef = useRef<Message[]>(messages);
  messagesRef.current = messages;

  const getBackendMessageId = (messageId: string) => {
    if (messageId.endsWith("_assistant") || messageId.endsWith("_user")) {
      return messageId.split("_")[0];
    }

    const completedAssistantPrefix = "completed_assistant_";
    if (messageId.startsWith(completedAssistantPrefix)) {
      return messageId.slice(completedAssistantPrefix.length);
    }

    const completedUserPrefix = "completed_user_";
    if (messageId.startsWith(completedUserPrefix)) {
      return messageId.slice(completedUserPrefix.length);
    }

    return null;
  };

  const getPromptForRegenerate = useCallback((messageId: string) => {
    const currentMessages = messagesRef.current;
    const messageIndex = currentMessages.findIndex(
      (message) => message.id === messageId
    );
    if (messageIndex === -1) return null;

    const targetMessage = currentMessages[messageIndex];
    const directPrompt =
      targetMessage?.originalPrompt?.trim() || targetMessage?.prompt?.trim();
    if (directPrompt) {
      return directPrompt;
    }

    for (let i = messageIndex - 1; i >= 0; i -= 1) {
      const candidate = currentMessages[i];
      if (candidate?.role === "user" && candidate.content.trim()) {
        return candidate.content.trim();
      }
    }

    return null;
  }, []);

  // Son mesajı takip etmek için ref
  const lastMessageRef = useRef<Message | null>(null);

  // Otomatik seslendirme - Yeni mesajlar geldiğinde
  // (legacy chat-store yerine ekranda render edilen gerçek mesaj listesini izle)
  useEffect(() => {
    if (!currentChatId || messages.length === 0) return;

    const lastMessage = messages[messages.length - 1];

    // Eğer yeni bir assistant mesajı geldi ve otomatik seslendirme aktifse
    if (
      lastMessage &&
      lastMessage.role === "assistant" &&
      lastMessage.content &&
      !lastMessage.isPending && // streaming devam ederken seslendirme başlatma
      isVoiceChatActive &&
      !isSpeaking &&
      lastMessageRef.current?.id !== lastMessage.id
    ) {
      // Son mesajı güncelle
      lastMessageRef.current = lastMessage;

      // Seslendirmeyi başlat
      speak(lastMessage.content);
    }
  }, [messages, currentChatId, speak, isVoiceChatActive, isSpeaking]);

  const handleMessageAction = useCallback(
    async (messageId: string, action: MessageAction) => {
      switch (action) {
        case "like":
        case "dislike": {
          if (!currentChatId) return;
          const cleanMessageId = getBackendMessageId(messageId);
          if (!cleanMessageId) return;

          const feedbackValue = action === "like" ? 1 : 2;
          // Mevcut feedback'i ekranda render edilen mesajlardan oku
          const currentMessage = messagesRef.current.find(
            (msg) => msg.id === messageId
          );
          const currentFeedback = currentMessage?.feedback ?? 0;
          const nextFeedback =
            currentFeedback === feedbackValue ? 0 : feedbackValue;

          try {
            const response = await updateMessageFeedback({
              messageId: cleanMessageId,
              feedback: nextFeedback,
            });

            const updatedFeedback = response?.data?.feedback;
            if (typeof updatedFeedback !== "number") return;

            queryClient.setQueriesData(
              { queryKey: QueryKeys.CONVERSATIONS.messages(currentChatId) },
              (oldData: any) => {
                if (!oldData) return oldData;

                if (Array.isArray(oldData.pages)) {
                  return {
                    ...oldData,
                    pages: oldData.pages.map((page: any) => ({
                      ...page,
                      data: {
                        ...page.data,
                        messages: page.data.messages.map((msg: any) =>
                          msg._id === cleanMessageId
                            ? { ...msg, feedback: updatedFeedback }
                            : msg
                        ),
                      },
                    })),
                  };
                }

                if (oldData.data?.chat) {
                  return {
                    ...oldData,
                    data: {
                      ...oldData.data,
                      chat: oldData.data.chat.map((msg: any) =>
                        msg._id === cleanMessageId
                          ? { ...msg, feedback: updatedFeedback }
                          : msg
                      ),
                    },
                  };
                }

                return oldData;
              }
            );
          } catch (apiError: any) {
            console.error("Feedback update error:", apiError);
            errorToast(apiError?.message || "Geri bildirim gönderilemedi");
          }
          break;
        }

        case "speak": {
          // Mesajı render edilen listeden ID'ye göre bul
          const messageToSpeak = messagesRef.current.find(
            (msg) => msg.id === messageId
          );
          if (messageToSpeak?.content) {
            await speak(messageToSpeak.content);
          }
          break;
        }

        case "stop":
          stopSpeaking();
          break;

        case "edit":
          // ✅ Generation mesajı için edit işlemi
          try {
            // ✅ Message ID'den suffix'i temizle (_assistant veya _user)
            // Backend'de message ID formatı: "690875695bdf813d3f081a62_assistant"
            // API'ye sadece gerçek ID'yi gönder: "690875695bdf813d3f081a62"
            const cleanMessageId = getBackendMessageId(messageId);
            if (!cleanMessageId) return;

            // API'den message input data'yı çek
            const editDataResult = await apiClient.get<MessageInputResponse>(
              API_ENDPOINTS.getMessageInput(cleanMessageId)
            );

            const editData = editDataResult.data;

            if (!editData || !editData.model || !editData.input) {
              throw new Error("Invalid edit data");
            }

            // Model ID'yi al
            const modelId = editData.model._id;
            const inputData = editData.input.inputData;

            // Output library'yi bul (response array'inden ilk library item'ı)
            const outputLibrary =
              editData.message?.response?.find(
                (r: any) => r.library && r.library.file
              )?.library || null;

            // Wizard'a navigate et - edit data ile
            const chatIdParam = currentChatId ? `/${currentChatId}` : "";
            navigate(`${modelsPaths.wizard(modelId)}${chatIdParam}`, {
              state: {
                editData: {
                  inputData,
                  outputLibrary,
                },
              },
            });
          } catch (error: any) {
            console.error("Edit action error:", error);
            errorToast(error?.message || "Edit işlemi başarısız oldu");
          }
          break;

        case "simplify": {
          if (!currentChatId) return;
          if (isStreaming) {
            errorToast(t("chat.streaming"));
            return;
          }

          const promptToSend = getPromptForRegenerate(messageId);
          if (!promptToSend) {
            errorToast(t("error.unknown"));
            return;
          }

          const modelToUse =
            selectedModelName || selectedModel?.name || undefined;
          const chatKey = getChatKey(currentChatId);
          setPendingUserMessage(
            chatKey,
            promptToSend,
            `pending_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`
          );

          try {
            await sendStreamingMessage(promptToSend, currentChatId, modelToUse);
          } catch (regenerateError) {
            setPendingUserMessage(chatKey, null, null);
            console.error("Regenerate action error:", regenerateError);
          }
          break;
        }

        default:
          console.warn("Unknown action:", action);
      }
    },
    [
      speak,
      stopSpeaking,
      currentChatId,
      navigate,
      errorToast,
      queryClient,
      updateMessageFeedback,
      getBackendMessageId,
      selectedModel,
      selectedModelName,
      sendStreamingMessage,
      setPendingUserMessage,
      getPromptForRegenerate,
      isStreaming,
      t,
    ]
  );

  return {
    handleMessageAction,
  };
};
